import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PatientInfoPanel = ({ patient, onUpdateVitals, onAddObservation }) => {
  const [activeTab, setActiveTab] = useState('vitals');
  const [newObservation, setNewObservation] = useState('');

  const vitals = [
    { label: 'Blood Pressure', value: '120/80 mmHg', status: 'normal', icon: 'Heart' },
    { label: 'Heart Rate', value: '72 bpm', status: 'normal', icon: 'Activity' },
    { label: 'Temperature', value: '98.6°F', status: 'normal', icon: 'Thermometer' },
    { label: 'Oxygen Saturation', value: '98%', status: 'normal', icon: 'Zap' },
    { label: 'Respiratory Rate', value: '16/min', status: 'normal', icon: 'Wind' },
    { label: 'Weight', value: '165 lbs', status: 'normal', icon: 'Scale' }
  ];

  const observations = [
    {
      id: 1,
      timestamp: new Date(Date.now() - 300000),
      observation: "Patient reports mild chest discomfort, 3/10 pain scale. No radiation to arms or jaw.",
      category: "Symptom Assessment"
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 180000),
      observation: "ECG shows normal sinus rhythm. No ST elevation or depression noted.",
      category: "Diagnostic Review"
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 120000),
      observation: "Patient demonstrates good understanding of medication instructions.",
      category: "Patient Education"
    }
  ];

  const allergies = [
    { name: 'Penicillin', severity: 'Severe', reaction: 'Anaphylaxis' },
    { name: 'Shellfish', severity: 'Moderate', reaction: 'Hives, swelling' },
    { name: 'Latex', severity: 'Mild', reaction: 'Contact dermatitis' }
  ];

  const medications = [
    { name: 'Lisinopril 10mg', frequency: 'Once daily', indication: 'Hypertension' },
    { name: 'Metformin 500mg', frequency: 'Twice daily', indication: 'Type 2 Diabetes' },
    { name: 'Atorvastatin 20mg', frequency: 'Once daily', indication: 'High cholesterol' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-success';
      case 'warning': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'severe': return 'bg-red-100 text-red-800';
      case 'moderate': return 'bg-yellow-100 text-yellow-800';
      case 'mild': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAddObservation = () => {
    if (!newObservation?.trim()) return;
    
    const observation = {
      id: observations?.length + 1,
      timestamp: new Date(),
      observation: newObservation,
      category: "Clinical Note"
    };
    
    onAddObservation(observation);
    setNewObservation('');
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const tabs = [
    { id: 'vitals', label: 'Vitals', icon: 'Activity' },
    { id: 'history', label: 'History', icon: 'FileText' },
    { id: 'observations', label: 'Notes', icon: 'Edit3' }
  ];

  return (
    <div className="w-80 bg-card border-l border-border flex flex-col h-full">
      {/* Patient Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={20} color="white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{patient?.name}</h3>
            <p className="text-sm text-muted-foreground">
              {patient?.age} years • {patient?.gender} • ID: {patient?.id}
            </p>
          </div>
        </div>
        
        <div className="mt-3 flex items-center space-x-4 text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Calendar" size={12} />
            <span>DOB: {patient?.dateOfBirth}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Phone" size={12} />
            <span>{patient?.phone}</span>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-2 text-sm font-medium transition-clinical ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'vitals' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-foreground">Current Vitals</h4>
              <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
                Update
              </Button>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {vitals?.map((vital, index) => (
                <div key={index} className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name={vital?.icon} size={16} className="text-primary" />
                      <span className="text-sm font-medium text-foreground">{vital?.label}</span>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${getStatusColor(vital?.status)}`}>
                        {vital?.value}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <h4 className="font-medium text-foreground mb-3">Quick Actions</h4>
              <div className="space-y-2">
                <Button variant="outline" size="sm" fullWidth iconName="Heart" iconPosition="left">
                  Record ECG
                </Button>
                <Button variant="outline" size="sm" fullWidth iconName="Thermometer" iconPosition="left">
                  Take Temperature
                </Button>
                <Button variant="outline" size="sm" fullWidth iconName="Activity" iconPosition="left">
                  Measure BP
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="p-4 space-y-6">
            {/* Allergies */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-warning" />
                <span>Allergies</span>
              </h4>
              <div className="space-y-2">
                {allergies?.map((allergy, index) => (
                  <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-red-900">{allergy?.name}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(allergy?.severity)}`}>
                        {allergy?.severity}
                      </span>
                    </div>
                    <p className="text-sm text-red-700 mt-1">{allergy?.reaction}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Current Medications */}
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
                <Icon name="Pill" size={16} className="text-primary" />
                <span>Current Medications</span>
              </h4>
              <div className="space-y-2">
                {medications?.map((med, index) => (
                  <div key={index} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{med?.name}</span>
                      <span className="text-xs text-muted-foreground">{med?.frequency}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{med?.indication}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'observations' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-foreground">Clinical Observations</h4>
              <span className="text-xs text-muted-foreground">{observations?.length} notes</span>
            </div>

            {/* Add New Observation */}
            <div className="bg-muted/50 rounded-lg p-3">
              <textarea
                value={newObservation}
                onChange={(e) => setNewObservation(e?.target?.value)}
                placeholder="Add clinical observation or note..."
                className="w-full h-20 bg-transparent border-0 resize-none text-sm text-foreground placeholder-muted-foreground focus:outline-none"
              />
              <div className="flex justify-end mt-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAddObservation}
                  disabled={!newObservation?.trim()}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Note
                </Button>
              </div>
            </div>

            {/* Existing Observations */}
            <div className="space-y-3">
              {observations?.map((obs) => (
                <div key={obs?.id} className="bg-card border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-primary">{obs?.category}</span>
                    <span className="text-xs text-muted-foreground">{formatTime(obs?.timestamp)}</span>
                  </div>
                  <p className="text-sm text-foreground">{obs?.observation}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientInfoPanel;